import React from 'react';
import { Button, Card, Col, Row, Space, Table, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { enterpriseMemoryService } from '@/services/enterpriseMemory.service';
import {
  HeroKpiCard,
  OiEmptyState,
  OiPageFrame,
  PrimaryKpiCard,
} from '@/components/owner-intelligence/OwnerIntelligenceComponents';

const { Text } = Typography;

/**
 * Organizational Knowledge (Enterprise Memory health) — distinct from Executive Memory
 * (/owner-intelligence/memory), which tracks executive health-score deltas.
 */
const OrganizationalKnowledgePage: React.FC = () => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['enterprise-memory-health-oi'],
    queryFn: () => enterpriseMemoryService.health(),
    staleTime: 60_000,
  });

  const contradictionsQ = useQuery({
    queryKey: ['enterprise-memory-contradictions-oi'],
    queryFn: async () => {
      const api = (await import('@/services/api.service')).default;
      const body = (await api.get('/enterprise-memory/contradictions?limit=20')) as { data?: any[] };
      return body.data || [];
    },
    staleTime: 60_000,
  });

  const health = data as Record<string, any> | undefined;
  const active =
    Number(health?.by_status?.ACTIVE || 0) + Number(health?.by_status?.PUBLISHED || 0);

  return (
    <OiPageFrame
      subtitle="Governed organizational memory health, gaps, contradictions, and usage — not executive KPI memory."
      loading={isLoading && !health}
      error={Boolean(error)}
      errorMessage="Organizational knowledge unavailable"
    >
      {!health ? (
        <OiEmptyState
          title="No memory health yet"
          body="Publish enterprise memory candidates from /ai/enterprise-memory to populate this view."
        />
      ) : (
        <>
          <div style={{ marginBottom: 12 }}>
            <Link to="/ai/enterprise-memory">
              <Button type="link">Open Enterprise Memory workspace</Button>
            </Link>
            <Button type="link" onClick={() => refetch()}>
              Refresh
            </Button>
            {health.jobs_disabled ? <Tag color="orange">Jobs disabled</Tag> : <Tag color="green">Jobs enabled</Tag>}
          </div>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={24} md={8}>
              <HeroKpiCard label="Active / published memories" value={active} />
            </Col>
            <Col xs={12} md={4}>
              <PrimaryKpiCard label="Candidate backlog" value={health.candidate_backlog ?? 0} />
            </Col>
            <Col xs={12} md={4}>
              <PrimaryKpiCard label="Review due 30d" value={health.review_due_30d ?? 0} />
            </Col>
            <Col xs={12} md={4}>
              <PrimaryKpiCard label="Unused 180d" value={health.unused_180d ?? 0} />
            </Col>
            <Col xs={12} md={4}>
              <PrimaryKpiCard label="Open contradictions" value={health.open_contradictions ?? 0} />
            </Col>
            <Col xs={12} md={4}>
              <PrimaryKpiCard
                label="Graph issues"
                value={
                  Number(health.graph?.dangling_to_memory || 0) +
                  Number(health.graph?.circular_pairs || 0) +
                  Number(health.graph?.broken_supersede_chains || 0)
                }
              />
            </Col>
          </Row>

          {Array.isArray(health.coverage) && health.coverage.length > 0 ? (
            <Card title="Knowledge coverage by domain" size="small" style={{ marginBottom: 16 }}>
              <Space wrap>
                {(health.coverage as Array<{ domain: string; coverage_pct: number; count: number }>).map((c) => (
                  <Tag key={c.domain}>
                    {c.domain}: {c.coverage_pct}% ({c.count})
                  </Tag>
                ))}
              </Space>
            </Card>
          ) : null}

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <PrimaryKpiCard label="Orphan ownership" value={health.orphan_ownership ?? 0} />
            </Col>
            <Col xs={12} md={6}>
              <PrimaryKpiCard label="Usage events 90d" value={health.usage_events_90d ?? 0} />
            </Col>
            <Col xs={12} md={6}>
              <PrimaryKpiCard
                label="Past effective_to still active"
                value={health.past_effective_to_still_active ?? 0}
              />
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Card title="Active by domain" size="small" style={{ marginBottom: 16 }}>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="domain"
                  dataSource={health.by_domain || []}
                  columns={[
                    { title: 'Domain', dataIndex: 'domain' },
                    { title: 'Count', dataIndex: 'count', width: 80 },
                  ]}
                  locale={{ emptyText: 'No published memory by domain' }}
                />
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Most used (90d)" size="small" style={{ marginBottom: 16 }}>
                <Table
                  size="small"
                  pagination={false}
                  rowKey="id"
                  dataSource={health.top_used_90d || []}
                  columns={[
                    {
                      title: 'Title',
                      dataIndex: 'title',
                      ellipsis: true,
                      render: (t: string, r: { id: string }) => (
                        <Link to={`/ai/enterprise-memory/${r.id}`}>{t}</Link>
                      ),
                    },
                    { title: 'Trust', dataIndex: 'trust_score', width: 70 },
                    { title: 'Uses', dataIndex: 'usage_cnt', width: 70 },
                  ]}
                  locale={{ emptyText: 'No usage yet' }}
                />
              </Card>
            </Col>
          </Row>

          <Card title="Open contradictions" size="small">
            <Table
              size="small"
              loading={contradictionsQ.isLoading}
              rowKey="id"
              dataSource={contradictionsQ.data || []}
              pagination={{ pageSize: 10 }}
              columns={[
                {
                  title: 'Status',
                  dataIndex: 'status',
                  width: 120,
                  render: (s: string) => <Tag>{s}</Tag>,
                },
                {
                  title: 'Notes',
                  dataIndex: 'notes',
                  ellipsis: true,
                },
                {
                  title: 'A',
                  dataIndex: 'memory_id_a',
                  width: 100,
                  render: (id: string) => (
                    <Link to={`/ai/enterprise-memory/${id}`}>{String(id).slice(0, 8)}…</Link>
                  ),
                },
                {
                  title: 'B',
                  dataIndex: 'memory_id_b',
                  width: 100,
                  render: (id: string) => (
                    <Link to={`/ai/enterprise-memory/${id}`}>{String(id).slice(0, 8)}…</Link>
                  ),
                },
              ]}
              locale={{ emptyText: 'No open contradictions' }}
            />
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              Knowledge gaps: backlog {health.candidate_backlog}, unused {health.unused_180d}, orphan owners{' '}
              {health.orphan_ownership}. Refreshed {health.refreshed_at}.
            </Text>
          </Card>
        </>
      )}
    </OiPageFrame>
  );
};

export default OrganizationalKnowledgePage;
